import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, Download, ChevronUp, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
type DocumentType = 'tenancy_agreement' | 'financing_agreement' | 'payment_authorization';
interface DocumentParty {
  role: string;
  name: string;
  email: string;
  signed: boolean;
  signedAt?: string;
}
interface DocumentData {
  id: string;
  type: DocumentType;
  title: string;
  createdAt: string;
  parties: DocumentParty[];
  content: {
    propertyAddress?: string;
    leaseDuration?: string;
    monthlyRent?: number;
    annualRent?: number;
    securityDeposit?: number;
    moveInDate?: string;
    totalAmount?: number;
    monthlyPayment?: number;
    paymentSchedule?: {
      month: number;
      dueDate: string;
      amount: number;
    }[];
    bankName?: string;
    accountNumber?: string;
  };
}
interface DocumentPreviewProps {
  document: DocumentData;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
}
export function DocumentPreview({
  document,
  zoom = 100,
  onZoomChange
}: DocumentPreviewProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 3;
  const handleZoomIn = () => {
    if (onZoomChange && zoom < 150) {
      onZoomChange(zoom + 25);
    }
  };
  const handleZoomOut = () => {
    if (onZoomChange && zoom > 50) {
      onZoomChange(zoom - 25);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const renderTenancyAgreement = () => <div className="space-y-6" data-id="element-923">
      <div className="text-center border-b border-gray-300 pb-6" data-id="element-924">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-925">
          RESIDENTIAL TENANCY AGREEMENT
        </h1>
        <p className="text-gray-600" data-id="element-926">
          Made pursuant to the Tenancy Law of Lagos State
        </p>
      </div>

      <section className="space-y-4" data-id="element-927">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-928">
          1. PARTIES TO THIS AGREEMENT
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-929">
          <p data-id="element-930">
            <strong data-id="element-931">LANDLORD:</strong>{' '}
            {document.parties.find(p => p.role === 'Landlord')?.name || 'N/A'}
          </p>
          <p data-id="element-932">
            <strong data-id="element-933">TENANT:</strong>{' '}
            {document.parties.find(p => p.role === 'Tenant')?.name || 'N/A'}
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-934">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-935">
          2. PROPERTY DETAILS
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-936">
          <p data-id="element-937">
            <strong data-id="element-938">Property Address:</strong>{' '}
            {document.content.propertyAddress || 'N/A'}
          </p>
          <p data-id="element-939">
            <strong data-id="element-940">Lease Duration:</strong>{' '}
            {document.content.leaseDuration || '12 months'}
          </p>
          <p data-id="element-941">
            <strong data-id="element-942">Commencement Date:</strong>{' '}
            {document.content.moveInDate ? formatDate(document.content.moveInDate) : 'N/A'}
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-943">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-944">
          3. RENT AND PAYMENT
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-945">
          <p data-id="element-946">
            <strong data-id="element-947">Annual Rent:</strong>{' '}
            {document.content.annualRent ? formatCurrency(document.content.annualRent) : 'N/A'}
          </p>
          <p data-id="element-948">
            <strong data-id="element-949">Security Deposit:</strong>{' '}
            {document.content.securityDeposit ? formatCurrency(document.content.securityDeposit) : 'N/A'}
          </p>
          <p data-id="element-950">
            The Tenant agrees to pay rent in accordance with the payment
            schedule facilitated by Renta Technologies Limited.
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-951">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-952">
          4. TENANT OBLIGATIONS
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700" data-id="element-953">
          <li data-id="element-954">Pay rent on or before the due date each month</li>
          <li data-id="element-955">Maintain the property in good condition</li>
          <li data-id="element-956">Not sublet without written consent from the Landlord</li>
          <li data-id="element-957">Report maintenance issues promptly</li>
          <li data-id="element-958">Comply with all applicable laws and regulations</li>
        </ul>
      </section>

      <section className="space-y-4" data-id="element-959">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-960">
          5. LANDLORD OBLIGATIONS
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700" data-id="element-961">
          <li data-id="element-962">Ensure the property is habitable and safe</li>
          <li data-id="element-963">Maintain structural integrity of the property</li>
          <li data-id="element-964">Respect tenant's right to quiet enjoyment</li>
          <li data-id="element-965">Provide reasonable notice before property inspections</li>
        </ul>
      </section>

      <section className="space-y-4" data-id="element-966">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-967">
          6. TERMINATION
        </h2>
        <p className="text-gray-700" data-id="element-968">
          Either party may terminate this agreement by providing 30 days written
          notice. Early termination by the Tenant may result in forfeiture of
          the security deposit and applicable early termination fees as outlined
          in the Financing Agreement.
        </p>
      </section>
    </div>;
  const renderFinancingAgreement = () => <div className="space-y-6" data-id="element-969">
      <div className="text-center border-b border-gray-300 pb-6" data-id="element-970">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-971">
          RENT FINANCING AGREEMENT
        </h1>
        <p className="text-gray-600" data-id="element-972">
          Between Tenant and Renta Technologies Limited
        </p>
      </div>

      <section className="space-y-4" data-id="element-973">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-974">
          1. PARTIES
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-975">
          <p data-id="element-976">
            <strong data-id="element-977">TENANT:</strong>{' '}
            {document.parties.find(p => p.role === 'Tenant')?.name || 'N/A'}
          </p>
          <p data-id="element-978">
            <strong data-id="element-979">FINANCIER:</strong> Renta Technologies Limited ("Renta")
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-980">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-981">
          2. FINANCING DETAILS
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-982">
          <p data-id="element-983">
            <strong data-id="element-984">Total Amount Financed:</strong>{' '}
            {document.content.totalAmount ? formatCurrency(document.content.totalAmount) : 'N/A'}
          </p>
          <p data-id="element-985">
            <strong data-id="element-986">Monthly Payment:</strong>{' '}
            {document.content.monthlyPayment ? formatCurrency(document.content.monthlyPayment) : 'N/A'}
          </p>
          <p data-id="element-987">
            <strong data-id="element-988">Payment Term:</strong> 12 months
          </p>
          <p data-id="element-989">
            <strong data-id="element-990">Service Fee:</strong> 25% of annual rent (included in
            monthly payments)
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-991">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-992">
          3. PAYMENT SCHEDULE
        </h2>
        <div className="overflow-x-auto" data-id="element-993">
          <table className="w-full text-sm text-gray-700" data-id="element-994">
            <thead data-id="element-995">
              <tr className="border-b border-gray-300" data-id="element-996">
                <th className="text-left py-2" data-id="element-997">Month</th>
                <th className="text-left py-2" data-id="element-998">Due Date</th>
                <th className="text-right py-2" data-id="element-999">Amount</th>
              </tr>
            </thead>
            <tbody data-id="element-1000">
              {(document.content.paymentSchedule || []).slice(0, 6).map(payment => <tr key={payment.month} className="border-b border-gray-200" data-id="element-1001">
                    <td className="py-2" data-id="element-1002">Month {payment.month}</td>
                    <td className="py-2" data-id="element-1003">{formatDate(payment.dueDate)}</td>
                    <td className="py-2 text-right" data-id="element-1004">
                      {formatCurrency(payment.amount)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4" data-id="element-1005">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1006">
          4. LATE PAYMENT
        </h2>
        <p className="text-gray-700" data-id="element-1007">
          A late fee of 5% of the monthly payment will be charged for payments
          received after the 5-day grace period. Continued non-payment may
          result in default and termination of the tenancy.
        </p>
      </section>

      <section className="space-y-4" data-id="element-1008">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1009">
          5. EARLY TERMINATION
        </h2>
        <p className="text-gray-700" data-id="element-1010">
          Early termination will require payment of all outstanding amounts plus
          an early termination fee of 10% of the remaining balance or ₦50,000,
          whichever is greater.
        </p>
      </section>
    </div>;
  const renderPaymentAuthorization = () => <div className="space-y-6" data-id="element-1011">
      <div className="text-center border-b border-gray-300 pb-6" data-id="element-1012">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1013">
          PAYMENT AUTHORIZATION
        </h1>
        <p className="text-gray-600" data-id="element-1014">Direct Debit Mandate</p>
      </div>

      <section className="space-y-4" data-id="element-1015">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1016">
          1. AUTHORIZATION
        </h2>
        <p className="text-gray-700" data-id="element-1017">
          I,{' '}
          <strong data-id="element-1018">
            {document.parties.find(p => p.role === 'Tenant')?.name || 'N/A'}
          </strong>
          , hereby authorize Renta Technologies Limited to debit my bank account
          for the monthly rent payments as specified in the Financing Agreement.
        </p>
      </section>

      <section className="space-y-4" data-id="element-1019">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1020">
          2. BANK DETAILS
        </h2>
        <div className="space-y-3 text-gray-700" data-id="element-1021">
          <p data-id="element-1022">
            <strong data-id="element-1023">Bank Name:</strong> {document.content.bankName || 'N/A'}
          </p>
          <p data-id="element-1024">
            <strong data-id="element-1025">Account Number:</strong>{' '}
            {document.content.accountNumber || '****1234'}
          </p>
        </div>
      </section>

      <section className="space-y-4" data-id="element-1026">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1027">
          3. DEBIT AMOUNT
        </h2>
        <p className="text-gray-700" data-id="element-1028">
          Monthly debit amount:{' '}
          <strong data-id="element-1029">
            {document.content.monthlyPayment ? formatCurrency(document.content.monthlyPayment) : 'N/A'}
          </strong>
        </p>
        <p className="text-gray-700" data-id="element-1030">
          Debits will occur on the 1st of each month, or the next business day
          if the 1st falls on a weekend or public holiday.
        </p>
      </section>

      <section className="space-y-4" data-id="element-1031">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1032">
          4. CANCELLATION
        </h2>
        <p className="text-gray-700" data-id="element-1033">
          This authorization remains in effect until the completion of all
          payments under the Financing Agreement, or until I provide written
          notice of cancellation to Renta with at least 14 days notice before
          the next scheduled debit.
        </p>
      </section>

      <section className="space-y-4" data-id="element-1034">
        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1035">
          5. INSUFFICIENT FUNDS
        </h2>
        <p className="text-gray-700" data-id="element-1036">
          I understand that if there are insufficient funds in my account on the
          debit date, Renta will attempt to debit the account again on days 2
          and 5 following the original due date. Failed debits may result in
          late fees and affect my credit score.
        </p>
      </section>
    </div>;
  const renderContent = () => {
    switch (document.type) {
      case 'tenancy_agreement':
        return renderTenancyAgreement();
      case 'financing_agreement':
        return renderFinancingAgreement();
      case 'payment_authorization':
        return renderPaymentAuthorization();
      default:
        return <p data-id="element-1037">Document type not supported</p>;
    }
  };
  return <div className="flex flex-col h-full bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden" data-id="element-1038">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-1039">
        <div className="flex items-center space-x-2" data-id="element-1040">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1041">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center space-x-1" data-id="element-1042">
            <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-1043">
              <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" data-id="element-1044" />
            </button>
            <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-1045">
              <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" data-id="element-1046" />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2" data-id="element-1047">
          <button onClick={handleZoomOut} disabled={zoom <= 50} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-1048">
            <ZoomOut className="h-4 w-4 text-gray-600 dark:text-gray-400" data-id="element-1049" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-center" data-id="element-1050">
            {zoom}%
          </span>
          <button onClick={handleZoomIn} disabled={zoom >= 150} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-1051">
            <ZoomIn className="h-4 w-4 text-gray-600 dark:text-gray-400" data-id="element-1052" />
          </button>
          <button className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1053">
            <Download className="h-4 w-4 text-gray-600 dark:text-gray-400" data-id="element-1054" />
          </button>
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-auto p-6" data-id="element-1055">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mx-auto bg-white shadow-lg rounded-lg" style={{
        width: `${8.5 * 96 * zoom / 100}px`,
        minHeight: `${11 * 96 * zoom / 100}px`,
        transform: `scale(${zoom / 100})`,
        transformOrigin: 'top center'
      }} data-id="element-1056">
          <div className="p-12" style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: '14px',
          lineHeight: '1.6'
        }} data-id="element-1057">
            {renderContent()}

            {/* Signature Section */}
            <div className="mt-12 pt-8 border-t border-gray-300" data-id="element-1058">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1059">
                SIGNATURES
              </h2>
              <div className="grid grid-cols-2 gap-8" data-id="element-1060">
                {document.parties.map(party => <div key={party.role} className="space-y-4" data-id="element-1061">
                    <p className="font-bold text-gray-900" data-id="element-1062">
                      {party.role.toUpperCase()}
                    </p>
                    <div className="border-b border-gray-400 h-16 flex items-end pb-2" data-id="element-1063">
                      {party.signed ? <span className="text-gray-600 italic" data-id="element-1064">
                          Signed electronically
                        </span> : <span className="text-gray-400" data-id="element-1065">Pending signature</span>}
                    </div>
                    <p className="text-gray-700" data-id="element-1066">{party.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-1067">
                      Date:{' '}
                      {party.signedAt ? formatDate(party.signedAt) : '_______________'}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}